import router from './../router'; // 根据实际路径调整
import verificationService from './verificationService';
import CryptoJS from 'crypto-js';
import { useMessage } from 'naive-ui';

// 加密配置
// 在您的Vue应用中 确保密钥长度为 16、24 或 32 字节，[注意] 请确保这个密钥是安全的，不要暴露在前端代码中
const AES_KEY = window.APP_CONFIG?.AES_KEY || process.env.VUE_APP_AES_KEY || 'bingo@408_AES_KEY_TEST';

// const AES_IV = CryptoJS.lib.WordArray.random(16);
const message = useMessage();

// 创建模拟 axios 实例的对象
const axiosInstance = {
  defaults: {
    headers: {
      common: {
        'Content-Type': 'application/json'
      }
    }
  },
  
  // 基础 URL
  baseURL: process.env.VUE_APP_API_BASE_URL,
  
  // 默认超时时间
  timeout: 60000,
  
  // 实现 axios 的 create 方法
  create(config = {}) {
    // 创建一个新实例，合并配置
    const newInstance = { ...this };
    
    // 合并配置
    newInstance.baseURL = config.baseURL || this.baseURL;
    newInstance.timeout = config.timeout || this.timeout;
    newInstance.defaults = {
      ...this.defaults,
      ...(config.defaults || {})
    };
    
    // 保留拦截器
    newInstance.interceptors = {
      request: { ...this.interceptors.request },
      response: { ...this.interceptors.response }
    };
    
    return newInstance;
  },
  
  // 实现请求拦截器和响应拦截器
  interceptors: {
    request: {
      handlers: [],
      use(onFulfilled, onRejected) {
        this.handlers.push({ onFulfilled, onRejected });
        return this.handlers.length - 1;
      },
      eject(id) {
        if (this.handlers[id]) {
          this.handlers[id] = null;
        }
      }
    },
    response: {
      handlers: [],
      use(onFulfilled, onRejected) {
        this.handlers.push({ onFulfilled, onRejected });
        return this.handlers.length - 1;
      },
      eject(id) {
        if (this.handlers[id]) {
          this.handlers[id] = null;
        }
      }
    }
  },
  
  // 实现基础请求方法
  async request(config) {
    // 重要修复：确保 config 和 headers 已初始化
    let currentConfig = {
      headers: { ...this.defaults.headers.common },
      ...config
    };
    
    // 确保 headers 存在
    if (!currentConfig.headers) {
      currentConfig.headers = { ...this.defaults.headers.common };
    }
    
    // 应用请求拦截器
    for (const handler of this.interceptors.request.handlers) {
      if (handler && handler.onFulfilled) {
        try {
          currentConfig = await handler.onFulfilled(currentConfig);
        } catch (error) {
          if (handler.onRejected) {
            return handler.onRejected(error);
          }
          throw error;
        }
      }
    }
    
    // 构建完整URL
    let url = currentConfig.url;
    if (!url.startsWith('http')) {
      url = `${this.baseURL || ''}${url}`;
    }
    
    // 处理查询参数
    if (currentConfig.params) {
      const queryString = new URLSearchParams();
      Object.entries(currentConfig.params).forEach(([key, value]) => {
        queryString.append(key, value);
      });
      const qs = queryString.toString();
      if (qs) {
        url = `${url}${url.includes('?') ? '&' : '?'}${qs}`;
      }
    }
    
    // 创建请求选项
    const options = {
      method: (currentConfig.method || 'get').toUpperCase(),
      headers: currentConfig.headers, // 使用拦截器处理后的headers
      credentials: 'include'
    };
    
    // 添加请求体
    if (['POST', 'PUT', 'PATCH'].includes(options.method) && currentConfig.data) {
      options.body = typeof currentConfig.data === 'string' 
        ? currentConfig.data 
        : JSON.stringify(currentConfig.data);
    }
    
    
    // 创建一个超时 Promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout of ${currentConfig.timeout || this.timeout}ms exceeded`));
      }, currentConfig.timeout || this.timeout);
    });
    
    try {
      // 执行请求，竞争超时
      const fetchPromise = fetch(url, options);
      const fetchResponse = await Promise.race([fetchPromise, timeoutPromise]);
      
      // 检查响应是否成功
      if (!fetchResponse.ok) {
        const error = new Error(`Request failed with status ${fetchResponse.status}`);
        // 尝试解析错误响应
        try {
          const contentType = fetchResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            error.response = {
              data: await fetchResponse.json(),
              status: fetchResponse.status,
              statusText: fetchResponse.statusText,
              headers: Object.fromEntries(fetchResponse.headers.entries()),
              config: currentConfig
            };
          } else {
            error.response = {
              data: await fetchResponse.text(),
              status: fetchResponse.status,
              statusText: fetchResponse.statusText,
              headers: Object.fromEntries(fetchResponse.headers.entries()),
              config: currentConfig
            };
          }
        } catch (parseError) {
          error.response = {
            data: { message: 'Could not parse error response' },
            status: fetchResponse.status,
            statusText: fetchResponse.statusText,
            headers: Object.fromEntries(fetchResponse.headers.entries()),
            config: currentConfig
          };
        }
        throw error;
      }
      
      // 处理流式响应
      if (currentConfig.responseType === 'stream' || currentConfig.streamHandler) {
        // 构造类似 axios 的响应对象，但保留原始流
        const response = {
          data: null, // 流式响应没有直接数据
          status: fetchResponse.status,
          statusText: fetchResponse.statusText,
          headers: Object.fromEntries(fetchResponse.headers.entries()),
          config: currentConfig,
          request: fetchResponse // 保留原始响应对象，包含 body 流
        };
        
        // 应用响应拦截器
        let finalResponse = response;
        for (const handler of this.interceptors.response.handlers) {
          if (handler && handler.onFulfilled) {
            try {
              finalResponse = await handler.onFulfilled(finalResponse);
            } catch (error) {
              if (handler.onRejected) {
                return handler.onRejected(error);
              }
              throw error;
            }
          }
        }
        
        return finalResponse;
      }
      
      // 处理常规响应
      let responseData;
      const contentType = fetchResponse.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await fetchResponse.json().catch(() => ({}));
      } else {
        responseData = await fetchResponse.text();
      }
      
      // 构造类似 axios 的响应对象
      const response = {
        data: responseData,
        status: fetchResponse.status,
        statusText: fetchResponse.statusText,
        headers: Object.fromEntries(fetchResponse.headers.entries()),
        config: currentConfig,
        request: fetchResponse
      };
      
      // 应用响应拦截器
      let finalResponse = response;
      
      for (const handler of this.interceptors.response.handlers) {
        if (handler && handler.onFulfilled) {
          try {
            finalResponse = await handler.onFulfilled(finalResponse);
          } catch (error) {
            if (handler.onRejected) {
              return handler.onRejected(error);
            }
            throw error;
          }
        }
      }
      
      return finalResponse;
    }
    catch (originalError) {
      // 应用响应错误拦截器
      let currentError = originalError;
      
      for (const handler of this.interceptors.response.handlers) {
        if (handler && handler.onRejected) {
          try {
            return await handler.onRejected(currentError);
          } catch (innerError) {
            currentError = innerError;  // 使用不同的变量名，避免修改 catch 参数
          }
        }
      }
      
      return Promise.reject(currentError);
    }    
    
  },
  
  // 实现 HTTP 方法
  async get(url, config = {}) {
    return this.request({ 
      ...config, 
      method: 'get', 
      url 
    });
  },
  
  async post(url, data, config = {}) {
    return this.request({ 
      ...config, 
      method: 'post', 
      url, 
      data 
    });
  },
  
  async put(url, data, config = {}) {
    return this.request({ 
      ...config, 
      method: 'put', 
      url, 
      data 
    });
  },
  
  async delete(url, config = {}) {
    return this.request({ 
      ...config, 
      method: 'delete', 
      url 
    });
  },
  
  async patch(url, data, config = {}) {
    return this.request({ 
      ...config, 
      method: 'patch', 
      url, 
      data 
    });
  }
};

// 添加请求拦截器
axiosInstance.interceptors.request.use(
  (config) => {
    try {
      // 添加时间戳到header
      config.headers['timestamp'] = new Date().getTime();

      if (!config.url.includes('/login') && !config.url.includes('/register')) {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.AuthToken = `Bearer ${token}`;
        }
      }

      // 加密逻辑
      let dataToEncrypt = null;
      if (config.method === 'get') {
        // 如果是get，直接将所有参数转换为字符串
        dataToEncrypt = Object.keys(config.params).map(key => `${key}=${config.params[key]}`).join('&');
      } else {
        dataToEncrypt = config.data;
      }
      if (dataToEncrypt) {
        const signature = CryptoJS.HmacSHA256(
          JSON.stringify(dataToEncrypt),
          CryptoJS.enc.Utf8.parse(AES_KEY)
        ).toString();
        config.headers['signature'] = signature;
      }

    } catch (error) {
      console.error('Encryption error:', error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 添加响应拦截器 - 与原始代码完全相同
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {  
    if (error.response) {
      const { status, data } = error.response;
      console.log(status, data);
      
      if (status === 403 && data.message === '用户已经被冻结') {
        console.log("用户已经被冻结");
        router.push('/user-frozen');
      } else if (status === 403 && data.message === '用户需要验证'){
        console.log("用户需要验证 axios");
        // 检查当前路由是否在排除列表中
        const currentPath = router.currentRoute.value.path;
        const excludedRoutes = ['/', '/login', '/forget-password'];
        
        if (!excludedRoutes.includes(currentPath)) {
          // 只有在非排除路由中才显示验证组件
          verificationService.showVerification();
        }
      } else if(status === 400 && data.message === '参数类型不匹配'){
        console.log("参数类型不匹配");
        message.error('参数类型不匹配');
        // 后退一步
        router.go(-1);
      }else if (status === 401) {
        console.error('Unauthorized, please login again.');
        // 这里如何提示信息哪？
        message.error('登录失败，请查看账号密码是否正确');
        router.push('/login');
      } else {
        console.error('An error occurred:', error.response);
      }
    } else {
      console.error('Network error:', error);
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
